import { forwardRef } from 'react'
import { styled } from '@portfolio/styles'
import { PortableText } from '@portfolio/components'
import { DashedLine } from '@portfolio/icons'
import { Step } from '@portfolio/models'

type StepCardProps = {
  step: Step
  index: number
  isLast: boolean
}

const StepCardContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$32',
  alignItems: 'flex-start',
  scrollSnapAlign: 'start',
  borderRadius: '$rBoxes',
  border: '1px solid #2D2D2D',
  position: 'relative',
  height: 'inherit',
  padding: '$30',
  minWidth: '290px',
  maxWidth: '290px',

  '@md': {
    border: 'none',
    padding: '0',
    minWidth: 'auto',
    maxWidth: 'auto',
  },
})

const StepNumber = styled('div', {
  display: 'flex',
  width: '80px',
  height: '80px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '40px',
  border: '1px solid $white',
  projectFont: 'ui02',
  color: '$white',
  textAlign: 'center',
  lineHeight: '120%',
})

const StepTitle = styled('h3', {
  projectFont: 'heading04',
  color: '$white',

  '@md': {
    projectFont: 'heading07',
    width: '260px',
    paddingRight: '60px',
    minWidth: '260px',
  },

  variants: {
    isLast: {
      true: {
        '@md': {
          width: '200px',
          paddingRight: '0',
          minWidth: '200px',
        },
      },
    },
  },
})

const StepContent = styled('div', {
  projectFont: 'heading08',
  color: '$gray300',
  fontWeight: 450,
  lineHeight: '120%',
  letterSpacing: '-0.18px',

  '& p': {
    margin: 0,
    padding: 0,
  },

  '& p + p': {
    marginTop: '22px',
  },

  '@md': {
    width: '260px',
    paddingRight: '60px',
    minWidth: '260px',
  },

  variants: {
    isLast: {
      true: {
        '@md': {
          width: '200px',
          paddingRight: '0',
          minWidth: '200px',
        },
      },
    },
  },
})

const NumberContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$30',
  width: '100%',
})

const IconContainer = styled('div', {
  display: 'none',

  '@md': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
})

export const StepCard = forwardRef<HTMLDivElement, StepCardProps>(
  ({ step, index, isLast }, ref) => {
    return (
      <StepCardContainer ref={ref}>
        <NumberContainer>
          <StepNumber>{String(index + 1).padStart(2, '0')}</StepNumber>
          {!isLast && (
            <IconContainer>
              <DashedLine />
            </IconContainer>
          )}
        </NumberContainer>
        {step.title && <StepTitle isLast={isLast}>{step.title}</StepTitle>}
        {step.content && (
          <StepContent isLast={isLast}>
            <PortableText value={step.content} appearance="plain" />
          </StepContent>
        )}
      </StepCardContainer>
    )
  },
)
